import { ArrowRightIcon } from '@heroicons/react/24/solid';

import { Button, EButtonType } from '@/components/Button';
import { selectBalloon, selectLoanTerm } from '@/context/borrower';
import {
    ELenderRepaymentFrequency,
    ILender,
    selectLenderRepaymentFrequency,
} from '@/context/lender';
import { useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { roundToTwoDecimalPlaces } from '@/utils/roundToTwoDecimalPlaces';

export interface IProps {
    lender: ILender;
    lowest?: boolean;
    baseId?: string;
    onChoose: () => void;
}

export const testId = 'FinanceOptionCard';

/**
 * One lender drawn as a single finance option.
 *
 * The figures are ordered by what a customer decides on — repayment first,
 * then the two rates, then the terms and costs — instead of the equally
 * weighted grid the standard card uses.
 */
export function FinanceOptionCard({
    lender,
    lowest,
    baseId,
    onChoose,
}: IProps): JSX.Element {
    // ***** Redux *****
    const repaymentFrequency = useAppSelector(selectLenderRepaymentFrequency);
    const loanTerm = useAppSelector(selectLoanTerm);
    const balloon = useAppSelector(selectBalloon);
    const copy = useCopy().financeOptions;

    // ***** Repayment *****
    const repayment =
        repaymentFrequency === ELenderRepaymentFrequency.MONTHLY
            ? lender.monthlyRepaymentAmount
            : repaymentFrequency === ELenderRepaymentFrequency.FORTNIGHTLY
            ? lender.fortnightlyRepaymentAmount
            : lender.weeklyRepaymentAmount;

    const repaymentSuffix =
        repaymentFrequency === ELenderRepaymentFrequency.MONTHLY
            ? '/month'
            : repaymentFrequency === ELenderRepaymentFrequency.FORTNIGHTLY
            ? '/fortnight'
            : '/week';

    const rows = [
        {
            label: copy.loanTermLabel,
            value: `${loanTerm} ${loanTerm === 1 ? 'year' : 'years'}`,
        },
        {
            label: copy.upfrontFeeLabel,
            value:
                lender.upfrontFeeAmount == null
                    ? '—'
                    : `$${lender.upfrontFeeAmount.toLocaleString()}`,
        },
        {
            label: copy.ongoingFeeLabel,
            value:
                lender.monthlyOngoingFeeAmount == null
                    ? '—'
                    : `$${roundToTwoDecimalPlaces(
                          lender.monthlyOngoingFeeAmount,
                      )} /month`,
        },
        { label: copy.balloonLabel, value: `${balloon ?? 0}%` },
    ];

    // ***** Render *****
    return (
        <div
            data-testid={testId}
            className='bg-white border border-cardEdge rounded-2xl p-4 _flex flex-col gap-3.5 text-left'
        >
            {/* Lender and badge */}
            <div className='_flex items-center justify-between gap-2'>
                <h3 className='text-[18px] font-bold text-panel'>
                    {lender.lenderName}
                </h3>
                {lowest && (
                    <span className='shrink-0 bg-reassureBg text-reassureText text-xs font-semibold rounded-full px-2.5 py-1'>
                        {copy.lowestBadge}
                    </span>
                )}
            </div>

            {/* Repayment — the figure people decide on */}
            <div>
                <p className='text-sm text-textBody'>
                    {copy.estimateLabel}
                </p>
                <p className='text-panel'>
                    <span className='text-[36px] leading-none font-bold'>
                        ${repayment?.toLocaleString() ?? '-'}
                    </span>
                    <span className='text-base text-textBody ml-1'>
                        {repaymentSuffix}
                    </span>
                </p>
            </div>

            {/* Rates */}
            <div className='grid grid-cols-2 gap-3'>
                {[
                    {
                        label: copy.advertisedLabel,
                        value: lender.advertisedRateInPercent,
                    },
                    {
                        label: copy.comparisonLabel,
                        value: lender.comparisonRateInPercent,
                    },
                ].map((rate) => (
                    <div
                        key={rate.label}
                        className='bg-optionTrack rounded-xl p-2.5'
                    >
                        <p className='text-[13px] text-textBody'>
                            {rate.label}
                        </p>
                        <p className='text-panel'>
                            {/* A rate the engine did not return is shown as
                                absent. Defaulting it to 0 would read as a
                                genuine 0% rate. */}
                            <span className='text-[19px] font-bold'>
                                {rate.value == null
                                    ? '—'
                                    : `${roundToTwoDecimalPlaces(rate.value)}%`}
                            </span>
                            {rate.value != null && (
                                <span className='text-[13px] text-textBody ml-1'>
                                    p.a.
                                </span>
                            )}
                        </p>
                    </div>
                ))}
            </div>

            {/* Terms and costs */}
            <dl className='_flex flex-col'>
                {rows.map((row, index) => (
                    <div
                        key={row.label}
                        className='_flex items-center justify-between py-2 border-t border-optionTrack first:border-t-0'
                    >
                        <dt
                            className={
                                index === 0
                                    ? 'text-sm text-textBody'
                                    : 'text-[13px] text-textBody'
                            }
                        >
                            {row.label}
                        </dt>
                        <dd
                            className={
                                index === 0
                                    ? 'text-sm font-bold text-panel'
                                    : 'text-[13px] font-semibold text-panel'
                            }
                        >
                            {row.value}
                        </dd>
                    </div>
                ))}
            </dl>

            {/* Button rather than a plain element: it is what reports the
                click, and this is the screen's conversion. */}
            <Button
                id={`${baseId}-button-chooseLender`}
                onClick={onChoose}
                type={EButtonType.CARD_PRIMARY}
            >
                <span className='_flex items-center gap-2 text-[15px]'>
                    {copy.cta}
                    <ArrowRightIcon className='h-4 w-4' />
                </span>
            </Button>
        </div>
    );
}
